/**
 * Password Strength Indicator Component - מחוון חוזק סיסמה
 * Visual indicator of password strength with Hebrew feedback
 */

import { Check, X } from 'lucide-react'

interface PasswordStrengthIndicatorProps {
  password: string
}

interface StrengthCheck {
  label: string
  test: (password: string) => boolean
}

const strengthChecks: StrengthCheck[] = [
  {
    label: 'לפחות 8 תווים',
    test: (pwd) => pwd.length >= 8,
  },
  {
    label: 'לפחות אות אחת',
    test: (pwd) => /[A-Za-z֐-׿]/.test(pwd),
  },
  {
    label: 'לפחות ספרה אחת (0-9)',
    test: (pwd) => /[0-9]/.test(pwd),
  },
]

// Optional bonus checks that boost strength but aren't required
const bonusChecks: StrengthCheck[] = [
  {
    label: '12 תווים או יותר',
    test: (pwd) => pwd.length >= 12,
  },
  {
    label: 'תו מיוחד (!@#$%^&*)',
    test: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
  },
]

export default function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  if (!password) {
    return null
  }

  const passedRequired = strengthChecks.filter((check) => check.test(password)).length
  const passedBonus = bonusChecks.filter((check) => check.test(password)).length
  const totalChecks = strengthChecks.length + bonusChecks.length
  const strength = ((passedRequired + passedBonus) / totalChecks) * 100
  const allRequired = passedRequired === strengthChecks.length

  // Determine strength level
  let strengthText = 'חלשה'
  let strengthColor = 'bg-red-500'
  let textColor = 'text-red-600'

  if (allRequired && passedBonus === bonusChecks.length) {
    strengthText = 'חזקה מאוד'
    strengthColor = 'bg-green-500'
    textColor = 'text-green-600'
  } else if (allRequired && passedBonus >= 1) {
    strengthText = 'חזקה'
    strengthColor = 'bg-blue-500'
    textColor = 'text-blue-600'
  } else if (allRequired) {
    strengthText = 'בינונית'
    strengthColor = 'bg-yellow-500'
    textColor = 'text-yellow-600'
  }

  return (
    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">חוזק הסיסמה:</span>
        <span className={`text-sm font-bold ${textColor}`} role="status">{strengthText}</span>
      </div>

      {/* Strength bar */}
      <div
        className="w-full bg-gray-200 rounded-full h-2 mb-3"
        role="progressbar"
        aria-valuenow={Math.round(strength)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`חוזק הסיסמה: ${strengthText}`}
      >
        <div
          className={`h-2 rounded-full transition-all duration-300 ${strengthColor}`}
          style={{ width: `${strength}%` }}
        />
      </div>

      {/* Requirements checklist */}
      <div className="space-y-1">
        {strengthChecks.map((check, index) => {
          const passed = check.test(password)
          return (
            <div key={`req-${index}`} className="flex items-center gap-2 text-xs">
              {passed ? (
                <Check size={14} className="text-green-600 flex-shrink-0" aria-hidden="true" />
              ) : (
                <X size={14} className="text-gray-400 flex-shrink-0" aria-hidden="true" />
              )}
              <span className={passed ? 'text-green-600' : 'text-gray-500'}>
                {check.label}
              </span>
            </div>
          )
        })}
        {bonusChecks.map((check, index) => {
          const passed = check.test(password)
          return (
            <div key={`bonus-${index}`} className="flex items-center gap-2 text-xs">
              {passed ? (
                <Check size={14} className="text-green-600 flex-shrink-0" aria-hidden="true" />
              ) : (
                <X size={14} className="text-gray-300 flex-shrink-0" aria-hidden="true" />
              )}
              <span className={passed ? 'text-green-600' : 'text-gray-400'}>
                {check.label} <span className="text-gray-400">(לחיזוק נוסף)</span>
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
